import React, { useState, useEffect } from 'react';
import '../screens/notes-screen.css';
import {
  Button,
  IconButton,
  Input,
  SearchInput,
  PasswordInput,
  Checkbox,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  Alert,
  SuccessAlert,
  ErrorAlert,
  WarningAlert,
  InfoAlert,
  Typography,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Body,
  Small,
  Caption,
  Label,
  Link,
  Logo,
  LogoIcon,
  LogoText,
  Sidebar,
  ResponsiveSidebar,
  MobileHeader,
  TaskCard,
  MeetingCard,
  NoteCard,
  CategoryCard,
  StatsCard,
  ProgressStats,
  ProductivityScore,
  UpcomingDeadlines,
  ChartContainer,
  ChartHeader,
  StatCard,
  ModernPieChart,
  ModernLineChart,
  ModernBarChart,
  ActivityHeatmap,
  MetricCard,
  PeakPerformanceHours,
  FocusTimeAnalysis,
  WeeklyGoalsProgress,
  RecentActivity,
  AIInsights,
  StatsGrid,
  ChartsGrid,
  designTokens,
  cn
} from './index';

/**
 * Comprehensive Design System Showcase with Dark Mode Support
 * Displays all components, variants, and states for the OrganiseWise design system
 */
export const DesignSystemShowcase = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showAlert, setShowAlert] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);

  // Scroll detection for collapsing sections
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateScrollState = () => {
      const scrollY = window.scrollY;
      const scrollDirection = scrollY > lastScrollY ? 'down' : 'up';
      
      // Header collapse logic
      if (scrollY > 100) {
        setIsScrolled(true);
        if (scrollDirection === 'down' && scrollY > 200) {
          setHeaderCollapsed(true);
        }
      } else {
        setIsScrolled(false);
        setHeaderCollapsed(false);
      }

      // Navigation collapse logic
      if (scrollY > 300 && scrollDirection === 'down') {
        setNavCollapsed(true);
      } else if (scrollDirection === 'up' && scrollY < 250) {
        setNavCollapsed(false);
      }

      lastScrollY = scrollY;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Component section wrapper
  const Section = ({ title, children, id }) => (
    <section id={id} className="mb-16">
      <div className="mb-8">
        <Heading2 className="border-b border-gray-200 dark:border-dark-300 pb-2 mb-4">{title}</Heading2>
      </div>
      {children}
    </section>
  );

  // Demo card wrapper
  const DemoCard = ({ title, description, children, className = "" }) => (
    <Card className={cn("mb-6", className)} variant={darkMode ? "dark-accent" : "default"}>
      <CardHeader>
        <div>
          <CardTitle>{title}</CardTitle>
          {description && (
            <Small color="secondary" className="mt-1">{description}</Small>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {children}
      </CardContent>
    </Card>
  );

  // Navigation for jumping to sections
  const Navigation = () => (
    <div
      className={cn(
        "sticky transition-all duration-300 ease-in-out mb-8 z-40",
        navCollapsed 
          ? "top-0 transform -translate-y-full opacity-0 pointer-events-none" 
          : "top-8 transform translate-y-0 opacity-100"
      )}
    >
      <Card className={cn(
        "transition-all duration-300",
        navCollapsed ? "scale-95" : "scale-100"
      )}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Components</CardTitle>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setNavCollapsed(!navCollapsed)}
              className="h-6 w-6 p-0"
            >
              <i className={cn(
                "fa-solid text-xs transition-transform duration-200",
                navCollapsed ? "fa-chevron-down" : "fa-chevron-up"
              )} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <nav className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {[
              { id: 'brand', label: 'Brand & Logo' },
              { id: 'typography', label: 'Typography' },
              { id: 'colors', label: 'Colors' },
              { id: 'buttons', label: 'Buttons' },
              { id: 'forms', label: 'Forms' },
              { id: 'cards', label: 'Cards' },
              { id: 'project-cards', label: 'Project Cards' },
              { id: 'charts', label: 'Charts & Analytics' },
              { id: 'navigation', label: 'Navigation' },
              { id: 'dashboard', label: 'Dashboard' },
              { id: 'alerts', label: 'Alerts' },
              { id: 'spacing', label: 'Spacing' },
            ].map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                className="text-primary-600 hover:text-primary-700 py-1 px-2 rounded hover:bg-primary-50 transition-colors"
                onClick={() => setNavCollapsed(true)}
              >
                {label}
              </a>
            ))}
          </nav>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className={cn(
      "min-h-screen p-4 md:p-8 transition-colors duration-300",
      darkMode ? "bg-dark-50 text-dark-900" : "bg-gray-50 text-gray-900"
    )}>
      <div className="max-w-7xl mx-auto">
        
        {/* Collapsible Header with Dark Mode Toggle */}
        <header className={cn(
          "text-center transition-all duration-500 ease-in-out mb-12",
          headerCollapsed 
            ? "transform -translate-y-8 opacity-0 scale-95 mb-4 pointer-events-none" 
            : "transform translate-y-0 opacity-100 scale-100"
        )}>
          {/* Sticky Dark Mode Toggle */}
          <div className={cn(
            "fixed top-4 right-4 z-50 transition-all duration-300",
            isScrolled ? "scale-90 opacity-90" : "scale-100 opacity-100"
          )}>
            <Button
              variant={darkMode ? "accent-gradient" : "primary"}
              onClick={toggleDarkMode}
              leftIcon={<i className={`fa-solid ${darkMode ? 'fa-sun' : 'fa-moon'}`} />}
              className={cn(
                "shadow-lg backdrop-blur-sm",
                darkMode ? "shadow-glow" : "shadow-xl",
                isScrolled && "ring-2 ring-white/20"
              )}
              size="sm"
            >
              {darkMode ? 'Light' : 'Dark'}
            </Button>
          </div>
          
          <Logo size="xl" showTagline className="justify-center mb-6" />
          <Heading1 className="mb-4">
            {darkMode ? (
              <>Design System Showcase - <span className="bg-accent-gradient bg-clip-text text-transparent">Dark Mode</span></>
            ) : (
              'Design System Showcase'
            )}
          </Heading1>
          <Body color="secondary" className="text-lg max-w-2xl mx-auto">
            A comprehensive showcase of all OrganiseWise design system components with {darkMode ? 'dark mode' : 'light mode'} styling, 
            variants, and states for consistent UI development.
          </Body>
          
          {/* Scroll Indicator */}
          <div className="mt-8 flex flex-col items-center space-y-2 opacity-50">
            <Small color="tertiary">Scroll down to see components collapse</Small>
            <div className="animate-bounce">
              <i className="fa-solid fa-chevron-down text-sm" />
            </div>
          </div>
        </header>

        {/* Expandable Header Button when collapsed */}
        {headerCollapsed && (
          <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-40">
            <Button
              size="sm"
              variant={darkMode ? "accent-outline" : "primary-outline"}
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setHeaderCollapsed(false);
              }}
              className="backdrop-blur-sm bg-white/80 dark:bg-dark-100/80 shadow-lg"
            >
              <i className="fa-solid fa-chevron-up mr-2" />
              Show Header
            </Button>
          </div>
        )}

        {/* Collapsible Navigation */}
        <Navigation />

        {/* Dark Mode Demo Section */}
        {darkMode && (
          <Section title="Dark Mode Features" id="dark-mode">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DemoCard title="Gradient Buttons" description="Accent gradient buttons with glow effects">
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3">
                    <Button variant="accent-gradient">
                      Accent Gradient
                    </Button>
                    <Button variant="accent-gradient" leftIcon={<i className="fa-solid fa-plus" />}>
                      Add Task
                    </Button>
                    <Button variant="accent" size="lg">
                      Large Accent
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-3">
                    <Button variant="cyan">
                      Cyan Button
                    </Button>
                    <Button variant="accent-outline">
                      Accent Outline
                    </Button>
                    <Button variant="cyan-outline">
                      Cyan Outline
                    </Button>
                  </div>
                </div>
              </DemoCard>

              <DemoCard title="Glow Effects" description="Components with beautiful glow effects">
                <div className="space-y-4">
                  <Card hover="glow" padding="sm" className="cursor-pointer">
                    <Small>Hover for Glow Effect</Small>
                  </Card>
                  
                  <Card variant="dark-gradient" padding="sm" shadow="glow">
                    <Small>Dark Gradient Card with Glow</Small>
                  </Card>
                  
                  <div className="flex gap-3">
                    <IconButton variant="accent" className="shadow-glow">
                      <i className="fa-solid fa-heart" />
                    </IconButton>
                    <IconButton variant="cyan" className="shadow-cyan-glow">
                      <i className="fa-solid fa-bookmark" />
                    </IconButton>
                  </div>
                </div>
              </DemoCard>
            </div>
          </Section>
        )}

        {/* Brand & Logo Section */}
        <Section title="Brand & Logo" id="brand">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DemoCard title="Logo Variants" description="Different logo variations for various use cases">
              <div className="space-y-6">
                <div className="space-y-4">
                  <Small color="secondary" className="uppercase tracking-wide">Horizontal Layout</Small>
                  <div className={cn("space-y-4 p-4 rounded border", darkMode ? "bg-dark-200 border-dark-300" : "bg-white border-gray-200")}>
                    <Logo size="sm" showTagline />
                    <Logo size="default" showTagline />
                    <Logo size="lg" showTagline />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Small color="secondary" className="uppercase tracking-wide">Vertical Layout</Small>
                  <div className={cn("flex space-x-8 p-4 rounded border", darkMode ? "bg-dark-200 border-dark-300" : "bg-white border-gray-200")}>
                    <Logo layout="vertical" size="sm" showTagline />
                    <Logo layout="vertical" size="default" showTagline />
                    <Logo layout="vertical" size="lg" showTagline />
                  </div>
                </div>
              </div>
            </DemoCard>

            <DemoCard title="Logo Components" description="Icon-only and text-only variations">
              <div className="space-y-6">
                <div className="space-y-4">
                  <Small color="secondary" className="uppercase tracking-wide">Icon Only</Small>
                  <div className={cn("flex items-center space-x-4 p-4 rounded border", darkMode ? "bg-dark-200 border-dark-300" : "bg-white border-gray-200")}>
                    <LogoIcon size="sm" />
                    <LogoIcon size="default" />
                    <LogoIcon size="lg" />
                    <LogoIcon size="xl" />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Small color="secondary" className="uppercase tracking-wide">Text Only</Small>
                  <div className={cn("space-y-3 p-4 rounded border", darkMode ? "bg-dark-200 border-dark-300" : "bg-white border-gray-200")}>
                    <LogoText size="sm" />
                    <LogoText size="default" showTagline />
                    <LogoText size="lg" />
                  </div>
                </div>
              </div>
            </DemoCard>
          </div>
        </Section>

        {/* Typography Section */}
        <Section title="Typography" id="typography">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DemoCard title="Heading Hierarchy" description="Semantic heading components">
              <div className="space-y-4">
                <Heading1>Heading 1 - Main Page Title</Heading1>
                <Heading2>Heading 2 - Section Title</Heading2>
                <Heading3>Heading 3 - Subsection Title</Heading3>
                <Heading4>Heading 4 - Small Heading</Heading4>
              </div>
            </DemoCard>

            <DemoCard title="Body Text Variants" description="Different text sizes and weights">
              <div className="space-y-4">
                <Body>Body text - Default paragraph text for general content and descriptions.</Body>
                <Small>Small text - Used for metadata, captions, and secondary information.</Small>
                <Caption>Caption text - The smallest text size for fine print and labels.</Caption>
                <Label>Label text - Medium weight text for form labels and emphasis.</Label>
                <Link as="button" onClick={() => alert('Link clicked!')}>
                  Link text - Interactive text with hover states
                </Link>
              </div>
            </DemoCard>

            <DemoCard title="Text Colors" description="Semantic color variations">
              <div className="space-y-3">
                <Typography variant="body" color="primary">Primary text color (default)</Typography>
                <Typography variant="body" color="secondary">Secondary text color</Typography>
                <Typography variant="body" color="tertiary">Tertiary text color</Typography>
                <Typography variant="body" color="brand">Brand colored text</Typography>
                <Typography variant="body" color="success">Success text color</Typography>
                <Typography variant="body" color="error">Error text color</Typography>
                <Typography variant="body" color="warning">Warning text color</Typography>
                <Typography variant="body" color="info">Info text color</Typography>
              </div>
            </DemoCard>

            <DemoCard title="Text Alignment" description="Alignment options">
              <div className="space-y-3 border rounded p-4">
                <Typography variant="body" align="left">Left aligned text (default)</Typography>
                <Typography variant="body" align="center">Center aligned text</Typography>
                <Typography variant="body" align="right">Right aligned text</Typography>
                <Typography variant="body" align="justify">
                  Justified text spreads content evenly across the available width, 
                  creating clean edges on both sides of the text block.
                </Typography>
              </div>
            </DemoCard>
          </div>
        </Section>

        {/* Colors Section */}
        <Section title="Colors" id="colors">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {darkMode ? (
              <>
                <DemoCard title="Dark Mode Colors" description="Dark theme color palette">
                  <div className="grid grid-cols-5 gap-3">
                    {Object.entries(designTokens.colors.dark).map(([shade, color]) => (
                      <div key={shade} className="text-center">
                        <div 
                          className="w-full h-16 rounded-lg mb-2 border border-dark-300"
                          style={{ backgroundColor: color }}
                        />
                        <Caption className="block">{shade}</Caption>
                        <Caption color="tertiary" className="block">{color}</Caption>
                      </div>
                    ))}
                  </div>
                </DemoCard>

                <DemoCard title="Accent Colors" description="Purple/violet accent colors">
                  <div className="grid grid-cols-5 gap-3">
                    {Object.entries(designTokens.colors.accent).map(([shade, color]) => (
                      <div key={shade} className="text-center">
                        <div 
                          className="w-full h-16 rounded-lg mb-2 border border-dark-300"
                          style={{ backgroundColor: color }}
                        />
                        <Caption className="block">{shade}</Caption>
                        <Caption color="tertiary" className="block">{color}</Caption>
                      </div>
                    ))}
                  </div>
                </DemoCard>

                <DemoCard title="Cyan Colors" description="Cyan secondary accent colors">
                  <div className="grid grid-cols-5 gap-3">
                    {Object.entries(designTokens.colors.cyan).map(([shade, color]) => (
                      <div key={shade} className="text-center">
                        <div 
                          className="w-full h-16 rounded-lg mb-2 border border-dark-300"
                          style={{ backgroundColor: color }}
                        />
                        <Caption className="block">{shade}</Caption>
                        <Caption color="tertiary" className="block">{color}</Caption>
                      </div>
                    ))}
                  </div>
                </DemoCard>
              </>
            ) : (
              <>
                <DemoCard title="Primary Colors" description="Main brand color palette">
                  <div className="grid grid-cols-5 gap-3">
                    {Object.entries(designTokens.colors.primary).map(([shade, color]) => (
                      <div key={shade} className="text-center">
                        <div 
                          className="w-full h-16 rounded-lg mb-2 border border-gray-200"
                          style={{ backgroundColor: color }}
                        />
                        <Caption className="block">{shade}</Caption>
                        <Caption color="tertiary" className="block">{color}</Caption>
                      </div>
                    ))}
                  </div>
                </DemoCard>

                <DemoCard title="Secondary Colors" description="Secondary brand color palette">
                  <div className="grid grid-cols-5 gap-3">
                    {Object.entries(designTokens.colors.secondary).map(([shade, color]) => (
                      <div key={shade} className="text-center">
                        <div 
                          className="w-full h-16 rounded-lg mb-2 border border-gray-200"
                          style={{ backgroundColor: color }}
                        />
                        <Caption className="block">{shade}</Caption>
                        <Caption color="tertiary" className="block">{color}</Caption>
                      </div>
                    ))}
                  </div>
                </DemoCard>
              </>
            )}

            <DemoCard title="Feedback Colors" description="Status and feedback colors">
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(designTokens.colors.feedback).map(([name, color]) => (
                  <div key={name} className="flex items-center space-x-3">
                    <div 
                      className="w-12 h-12 rounded-lg border border-gray-200 dark:border-dark-300"
                      style={{ backgroundColor: color }}
                    />
                    <div>
                      <Small className="block capitalize font-medium">{name}</Small>
                      <Caption color="tertiary">{color}</Caption>
                    </div>
                  </div>
                ))}
              </div>
            </DemoCard>
          </div>
        </Section>

        {/* Buttons Section */}
        <Section title="Buttons" id="buttons">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DemoCard title="Button Variants" description="Different button styles and purposes">
              <div className="space-y-6">
                {darkMode ? (
                  <>
                    <div className="space-y-3">
                      <Small color="secondary" className="uppercase tracking-wide">Dark Mode Variants</Small>
                      <div className="flex flex-wrap gap-3">
                        <Button variant="accent-gradient">Accent Gradient</Button>
                        <Button variant="accent">Accent</Button>
                        <Button variant="cyan">Cyan</Button>
                        <Button variant="danger">Danger</Button>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <Small color="secondary" className="uppercase tracking-wide">Outline Variants</Small>
                      <div className="flex flex-wrap gap-3">
                        <Button variant="accent-outline">Accent Outline</Button>
                        <Button variant="cyan-outline">Cyan Outline</Button>
                        <Button variant="gray-outline">Gray Outline</Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-3">
                      <Small color="secondary" className="uppercase tracking-wide">Filled Buttons</Small>
                      <div className="flex flex-wrap gap-3">
                        <Button variant="primary">Primary</Button>
                        <Button variant="secondary">Secondary</Button>
                        <Button variant="success">Success</Button>
                        <Button variant="danger">Danger</Button>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <Small color="secondary" className="uppercase tracking-wide">Outline Buttons</Small>
                      <div className="flex flex-wrap gap-3">
                        <Button variant="primary-outline">Primary</Button>
                        <Button variant="secondary-outline">Secondary</Button>
                        <Button variant="gray-outline">Gray</Button>
                      </div>
                    </div>
                  </>
                )}
                
                <div className="space-y-3">
                  <Small color="secondary" className="uppercase tracking-wide">Ghost Buttons</Small>
                  <div className="flex flex-wrap gap-3">
                    <Button variant="ghost">Ghost</Button>
                    <Button variant={darkMode ? "ghost-accent" : "ghost-primary"}>
                      {darkMode ? "Ghost Accent" : "Ghost Primary"}
                    </Button>
                  </div>
                </div>
              </div>
            </DemoCard>

            <DemoCard title="Button Sizes" description="Different sizes for various contexts">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-4">
                  <Button size="sm">Small</Button>
                  <Button size="default">Default</Button>
                  <Button size="lg">Large</Button>
                </div>
              </div>
            </DemoCard>

            <DemoCard title="Buttons with Icons" description="Icons enhance button meaning and usability">
              <div className="space-y-6">
                <div className="space-y-3">
                  <Small color="secondary" className="uppercase tracking-wide">Left Icons</Small>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      variant={darkMode ? "accent-gradient" : "primary"} 
                      leftIcon={<i className="fa-solid fa-plus" />}
                    >
                      Add Task
                    </Button>
                    <Button 
                      variant={darkMode ? "cyan" : "secondary"} 
                      rightIcon={<i className="fa-solid fa-arrow-right" />}
                    >
                      Continue
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Small color="secondary" className="uppercase tracking-wide">Right Icons</Small>
                  <div className="flex flex-wrap gap-3">
                    <Button rightIcon={<i className="fa-solid fa-arrow-right" />}>
                      Continue
                    </Button>
                    <Button variant="primary-outline" rightIcon={<i className="fa-solid fa-external-link" />}>
                      Open Link
                    </Button>
                  </div>
                </div>
              </div>
            </DemoCard>

            <DemoCard title="Icon Buttons & States" description="Compact buttons and special states">
              <div className="space-y-6">
                <div className="space-y-3">
                  <Small color="secondary" className="uppercase tracking-wide">Icon Only Buttons</Small>
                  <div className="flex flex-wrap gap-3">
                    <IconButton variant={darkMode ? "accent" : "primary"}>
                      <i className="fa-solid fa-heart" />
                    </IconButton>
                    <IconButton variant={darkMode ? "cyan" : "secondary"}>
                      <i className="fa-solid fa-bookmark" />
                    </IconButton>
                    <IconButton size="icon-sm">
                      <i className="fa-solid fa-pencil" />
                    </IconButton>
                    <IconButton size="icon-lg" variant="danger">
                      <i className="fa-solid fa-trash" />
                    </IconButton>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Small color="secondary" className="uppercase tracking-wide">Button States</Small>
                  <div className="flex flex-wrap gap-3">
                    <Button loading>Loading...</Button>
                    <Button disabled>Disabled</Button>
                  </div>
                </div>
              </div>
            </DemoCard>
          </div>
        </Section>

        {/* Forms Section */}
        <Section title="Forms" id="forms">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DemoCard title="Input Variations" description="Different input types and configurations">
              <div className="space-y-4 max-w-sm">
                <Input
                  label="Full Name"
                  placeholder="Enter your full name"
                  required
                  variant={darkMode ? "dark-accent" : "default"}
                />
                
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  error={email && !email.includes('@') ? 'Please enter a valid email' : ''}
                  helperText="We'll never share your email"
                  variant={darkMode ? "dark-accent" : "default"}
                />
                
                <Input
                  label="Website URL"
                  type="url"
                  placeholder="https://example.com"
                  leftIcon={<i className="fa-solid fa-globe" />}
                  variant={darkMode ? "dark-accent" : "default"}
                />
                
                <SearchInput 
                  placeholder="Search anything..." 
                  variant={darkMode ? "dark-accent" : "default"}
                />
                
                <Input
                  label="Phone Number"
                  type="tel"
                  placeholder="(555) 123-4567"
                  rightIcon={<i className="fa-solid fa-phone" />}
                />
              </div>
            </DemoCard>

            <DemoCard title="Input States" description="Various input states and feedback">
              <div className="space-y-4 max-w-sm">
                <Input
                  label="Default State"
                  placeholder="Normal input"
                  variant={darkMode ? "dark-accent" : "default"}
                />
                
                <Input
                  label="Success State"
                  placeholder="Valid input"
                  variant="success"
                  helperText="Perfect! This looks good."
                />
                
                <Input
                  label="Error State"
                  placeholder="Invalid input"
                  error="This field is required"
                />
                
                <Input
                  label="Disabled State"
                  placeholder="Cannot edit"
                  disabled
                  value="Read-only value"
                />
                
                <Input
                  label="With Icons"
                  placeholder="Search users..."
                  leftIcon={<i className="fa-solid fa-search" />}
                  rightIcon={<i className="fa-solid fa-filter" />}
                  variant={darkMode ? "dark-accent" : "default"}
                />
              </div>
            </DemoCard>

            <DemoCard title="Input Sizes" description="Different sizes for various contexts">
              <div className="space-y-4">
                <Input
                  size="sm"
                  placeholder="Small input"
                  label="Small Size"
                />
                
                <Input
                  size="default"
                  placeholder="Default input"
                  label="Default Size"
                />
                
                <Input
                  size="lg"
                  placeholder="Large input"
                  label="Large Size"
                />
              </div>
            </DemoCard>

            <DemoCard title="Password Input" description="Password input with show/hide toggle">
              <div className="space-y-4 max-w-sm">
                <PasswordInput
                  label="Password"
                  placeholder="Enter your password"
                  variant={darkMode ? "dark-accent" : "default"}
                />
                
                <PasswordInput
                  label="Confirm Password"
                  placeholder="Confirm your password"
                  showPasswordToggle={true}
                  variant={darkMode ? "dark-accent" : "default"}
                />
                
                <PasswordInput
                  label="Current Password"
                  placeholder="Enter current password"
                  error="Password must be at least 8 characters"
                />
              </div>
            </DemoCard>

            <DemoCard title="Checkbox Components" description="Checkbox variations and states">
              <div className="space-y-4">
                <Checkbox
                  label="Remember me"
                />
                
                <Checkbox
                  label="I agree to the terms and conditions"
                  helperText="Please read the terms carefully"
                />
                
                <Checkbox
                  label="Subscribe to newsletter"
                  error="This field is required"
                />
                
                <Checkbox
                  label="Enable notifications"
                  disabled
                />
                
                <div className="space-y-2">
                  <Typography variant="label">Checkbox Sizes:</Typography>
                  <div className="space-y-2">
                    <Checkbox label="Small checkbox" size="sm" />
                    <Checkbox label="Default checkbox" size="default" />
                    <Checkbox label="Large checkbox" size="lg" />
                  </div>
                </div>
              </div>
            </DemoCard>

            <DemoCard title="Login Form Example" description="Complete login form using design system components">
              <div className="max-w-sm space-y-4">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl mb-4 shadow-glow animate-pulse-glow mx-auto">
                    <i className="fa-solid fa-brain text-white text-2xl"></i>
                  </div>
                  <Typography variant="h3" className="mb-2">Welcome back</Typography>
                  <Typography color="secondary" className="text-sm">Sign in to your account</Typography>
                </div>
                
                <button className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition duration-200 mb-4 shadow-sm hover:shadow-md">
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
                
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">or continue with email</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
                  <input 
                    type="email" 
                    placeholder="Enter your email" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition duration-200"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <div className="relative">
                    <input 
                      type="password" 
                      placeholder="Enter your password" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition duration-200 pr-12"
                    />
                    <button type="button" className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <i className="fa-solid fa-eye"></i>
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input type="checkbox" className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500" />
                    <span className="ml-2 text-sm text-gray-600">Remember me</span>
                  </label>
                  <span className="text-sm text-primary-500 hover:text-primary-600 transition cursor-pointer">Forgot password?</span>
                </div>
                
                <button className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3 px-4 rounded-xl hover:from-primary-600 hover:to-primary-700 transition duration-200 font-medium shadow-glow">
                  Sign in
                </button>
                
                <div className="text-center">
                  <Typography color="secondary" className="text-sm">
                    Don't have an account?{' '}
                    <span className="text-primary-500 hover:text-primary-600 font-medium transition cursor-pointer">Sign up</span>
                  </Typography>
                </div>
              </div>
            </DemoCard>
          </div>
        </Section>

        {/* Cards Section */}
        <Section title="Cards" id="cards">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <DemoCard title="Card Variants" description="Different card styles and effects">
              <div className="space-y-4">
                <Card variant={darkMode ? "dark-accent" : "default"} padding="sm">
                  <Small>{darkMode ? "Dark Accent Card" : "Default Card"}</Small>
                </Card>
                
                {darkMode && (
                  <Card variant="dark-gradient" padding="sm" shadow="glow">
                    <Small>Dark Gradient with Glow</Small>
                  </Card>
                )}
                
                <Card hover="glow" padding="sm" className="cursor-pointer">
                  <Small>Hover for Glow Effect</Small>
                </Card>
                
                <Card hover="lift" padding="sm" className="cursor-pointer">
                  <Small>Hover to Lift</Small>
                </Card>
              </div>
            </DemoCard>

            <Card className="md:col-span-2 lg:col-span-2" variant={darkMode ? "dark-accent" : "default"}>
              <CardHeader>
                <CardTitle>Complete Card Example</CardTitle>
              </CardHeader>
              <CardContent>
                <Body className="mb-4">
                  This is a complete card example showing the header, content, and footer structure 
                  with proper typography and spacing in {darkMode ? 'dark' : 'light'} mode.
                </Body>
                <div className="flex items-center space-x-4 mb-4">
                  <div className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center",
                    darkMode ? "bg-accent-500/20" : "bg-primary-100"
                  )}>
                    <i className={cn(
                      "fa-solid fa-user",
                      darkMode ? "text-accent-500" : "text-primary-600"
                    )} />
                  </div>
                  <div>
                    <Small className="font-medium">John Doe</Small>
                    <Caption color="secondary" className="block">Product Manager</Caption>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button size="sm" variant={darkMode ? "accent-outline" : "primary-outline"}>
                  View Profile
                </Button>
                <Button size="sm" variant={darkMode ? "accent-gradient" : "primary"}>
                  Send Message
                </Button>
              </CardFooter>
            </Card>
          </div>
        </Section>

        {/* Project Cards Section */}
        <Section title="Project Cards" id="project-cards">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <DemoCard title="Task Cards" description="Cards for task management with progress tracking">
              <div className="space-y-4">
                <TaskCard
                  title="Update landing page content"
                  description="Review and update the marketing copy for the new product launch landing page."
                  priority="medium"
                  progress={2}
                  maxProgress={5}
                  assignee={{ name: "John Doe" }}
                  dueDate="Due in 2 days"
                />
                
                <TaskCard
                  title="Fix critical bug"
                  description="Resolve authentication issue affecting user login process."
                  priority="high"
                  progress={4}
                  maxProgress={5}
                  assignee={{ name: "Sarah Smith" }}
                  dueDate="Due today"
                />
              </div>
            </DemoCard>

            <DemoCard title="Meeting Cards" description="Cards for meeting scheduling and details">
              <div className="space-y-4">
                <MeetingCard
                  title="Weekly Team Sync"
                  description="Discuss project progress, blockers, and upcoming milestones."
                  date="Today"
                  startTime="2:00 PM"
                  endTime="3:00 PM"
                  attendees={[
                    { name: "John" },
                    { name: "Sarah" },
                    { name: "Mike" }
                  ]}
                  additionalAttendees={2}
                />
                
                <MeetingCard
                  title="Product Review"
                  description="Review latest features and gather feedback from stakeholders."
                  date="Tomorrow"
                  startTime="10:00 AM"
                  endTime="11:30 AM"
                  attendees={[
                    { name: "Product Team" }
                  ]}
                />
              </div>
            </DemoCard>

            <DemoCard title="Note Cards" description="Enhanced cards for notes with modern design and interactions">
              <div className="space-y-6">
                {/* Modern Note Card Examples */}
                <div className="space-y-4">
                  <Small color="secondary" className="uppercase tracking-wide">Note Card Variations</Small>
                  
                  {/* Connected Note Card */}
                  <div className="note-card" style={{ '--category-color': darkMode ? '#8b5cf6' : '#3b82f6' }}>
                    <div className="note-card-header">
                      <div className="note-actions">
                        <IconButton size="icon-sm" variant="ghost" title="Share">
                          <i className="fa-solid fa-share-alt" />
                        </IconButton>
                        <IconButton size="icon-sm" variant="ghost" title="Link">
                          <i className="fa-solid fa-link" />
                        </IconButton>
                        <IconButton size="icon-sm" variant="ghost" title="Edit">
                          <i className="fa-solid fa-edit" />
                        </IconButton>
                        <IconButton size="icon-sm" variant="ghost" title="Delete">
                          <i className="fa-solid fa-trash" />
                        </IconButton>
                      </div>
                      <h3 className="note-title">Design System Implementation Notes</h3>
                    </div>
                    
                    <div className="note-content">
                      <p className="note-summary">
                        Collection of design patterns and implementation guidelines for the new design system. 
                        Includes component specifications, spacing guidelines, and accessibility considerations.
                      </p>
                    </div>
                    
                    <div className="note-footer">
                      <div className="note-date">
                        <i className="fa-solid fa-clock" />
                        <span>2 days ago</span>
                      </div>
                      <div className="note-tags">
                        <span className="note-tag">
                          <i className="fa-solid fa-tag" />
                          Design
                        </span>
                        <span className="note-tag">
                          <i className="fa-solid fa-tag" />
                          System
                        </span>
                      </div>
                    </div>
                    
                    <div className="task-connection connected">
                      <i className="fa-solid fa-tasks" />
                      <span>Connected to task</span>
                      <div className="task-priority-dot high" />
                    </div>
                  </div>

                  {/* Unconnected Note Card */}
                  <div className="note-card" style={{ '--category-color': darkMode ? '#22d3ee' : '#10b981' }}>
                    <div className="note-card-header">
                      <div className="note-actions">
                        <IconButton size="icon-sm" variant="ghost" title="Share">
                          <i className="fa-solid fa-share-alt" />
                        </IconButton>
                        <IconButton size="icon-sm" variant="ghost" title="Link">
                          <i className="fa-solid fa-link" />
                        </IconButton>
                        <IconButton size="icon-sm" variant="ghost" title="Edit">
                          <i className="fa-solid fa-edit" />
                        </IconButton>
                        <IconButton size="icon-sm" variant="ghost" title="Delete">
                          <i className="fa-solid fa-trash" />
                        </IconButton>
                      </div>
                      <h3 className="note-title">Meeting Ideas for Q4 Planning</h3>
                    </div>
                    
                    <div className="note-content">
                      <p className="note-summary">
                        Brainstorming session notes for quarterly planning meeting. Ideas for improving team productivity 
                        and new feature development roadmap for the next quarter.
                      </p>
                    </div>
                    
                    <div className="note-footer">
                      <div className="note-date">
                        <i className="fa-solid fa-clock" />
                        <span>1 week ago</span>
                      </div>
                      <div className="note-tags">
                        <span className="note-tag">
                          <i className="fa-solid fa-tag" />
                          Meeting
                        </span>
                      </div>
                    </div>
                    
                    <div className="task-connection empty">
                      <i className="fa-solid fa-link" />
                      <span>Connect to a task</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Small color="secondary" className="uppercase tracking-wide">Design Features</Small>
                  <div className={cn("p-4 rounded border", darkMode ? "bg-dark-200 border-dark-300" : "bg-white border-gray-200")}>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <i className="fa-solid fa-check text-green-500" />
                        <span>Floating action icons with glassmorphism effect</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <i className="fa-solid fa-check text-green-500" />
                        <span>Category color borders with gradient separators</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <i className="fa-solid fa-check text-green-500" />
                        <span>Enhanced hover effects with smooth animations</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <i className="fa-solid fa-check text-green-500" />
                        <span>Task connection indicators with priority dots</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <i className="fa-solid fa-check text-green-500" />
                        <span>Responsive design with mobile optimizations</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <i className="fa-solid fa-check text-green-500" />
                        <span>Proper title truncation and content management</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </DemoCard>
          </div>
        </Section>

        {/* Charts & Analytics Section */}
        <Section title="Charts & Analytics" id="charts">
          <div className="space-y-8">
            
            {/* Stat Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DemoCard title="Stat Cards" description="Statistical cards with icons and trend indicators">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <StatCard
                    label="Total Tasks"
                    value={28}
                    change="+12%"
                    changeType="positive"
                    trend="from last week"
                    icon={<i className="fa-solid fa-list-check text-lg" />}
                    iconColor="primary"
                  />
                  
                  <StatCard
                    label="Completion Rate"
                    value="85.5%"
                    change="+8%"
                    changeType="positive"
                    trend="from last week"
                    icon={<i className="fa-solid fa-percent text-lg" />}
                    iconColor="success"
                  />
                </div>
              </DemoCard>

              <DemoCard title="Metric Cards" description="Metrics with mini charts">
                <MetricCard
                  value="4.2h"
                  label="Average Focus Time"
                  change="+15%"
                  changeType="positive"
                  icon={<i className="fa-solid fa-clock text-lg" />}
                  trend="Higher than last week"
                  chartData={{
                    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
                    datasets: [{
                      data: [3.2, 4.1, 3.8, 4.5, 4.2],
                      borderColor: designTokens.colors.primary[600],
                      backgroundColor: `${designTokens.colors.primary[600]}20`,
                    }]
                  }}
                  chartType="line"
                />
              </DemoCard>
            </div>

            {/* Pie and Bar Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DemoCard title="Pie Chart" description="Category distribution with legends">
                <ModernPieChart
                  title="Task Categories"
                  data={{
                    labels: ['Work', 'Personal', 'Shopping', 'Health', 'Learning'],
                    datasets: [{
                      data: [35, 25, 15, 12, 13],
                      backgroundColor: [
                        designTokens.colors.primary[600],
                        designTokens.colors.secondary[600],
                        designTokens.colors.feedback.warning,
                        designTokens.colors.feedback.danger,
                        designTokens.colors.feedback.success,
                      ],
                      borderColor: '#FFFFFF',
                      borderWidth: 2,
                    }]
                  }}
                  showLegend={true}
                />
              </DemoCard>

              <DemoCard title="Bar Chart" description="Task activity by hour">
                <ModernBarChart
                  title="Task Activity by Hour"
                  data={{
                    labels: ['9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM', '3 PM', '4 PM'],
                    datasets: [{
                      label: 'Tasks Completed',
                      data: [3, 7, 5, 4, 6, 8, 5, 3],
                      backgroundColor: [
                        '#10B981', '#10B981', '#F59E0B', '#F59E0B', 
                        '#EF4444', '#EF4444', '#F59E0B', '#10B981'
                      ],
                      borderColor: '#FFFFFF',
                      borderWidth: 1,
                      borderRadius: 6,
                    }]
                  }}
                  rounded={true}
                />
              </DemoCard>
            </div>

            {/* Line Chart */}
            <DemoCard title="Line Chart" description="Productivity trends over time">
              <ModernLineChart
                title="Productivity Trends"
                data={{
                  labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
                  datasets: [
                    {
                      label: 'Completed Tasks',
                      data: [5, 8, 6, 7, 9, 4, 3],
                      borderColor: '#10B981',
                      backgroundColor: '#10B98120',
                      borderWidth: 3,
                      fill: false,
                      tension: 0.4,
                      pointBackgroundColor: '#10B981',
                      pointBorderColor: '#FFFFFF',
                      pointBorderWidth: 2,
                      pointRadius: 4,
                    },
                    {
                      label: 'Pending Tasks',
                      data: [3, 5, 4, 6, 4, 2, 1],
                      borderColor: '#F59E0B',
                      backgroundColor: '#F59E0B20',
                      borderWidth: 3,
                      fill: false,
                      tension: 0.4,
                      pointBackgroundColor: '#F59E0B',
                      pointBorderColor: '#FFFFFF',
                      pointBorderWidth: 2,
                      pointRadius: 4,
                    }
                  ]
                }}
                gradient={true}
                smooth={true}
              />
            </DemoCard>

            {/* Activity Heatmap */}
            <DemoCard title="Activity Heatmap" description="Task completion patterns by time">
              <ActivityHeatmap
                title="Activity Heatmap"
                subtitle="Daily task completion patterns by hour"
                data={{
                  heatmap_data: [
                    { date: '2024-01-01', hour: 9, completed_count: 2 },
                    { date: '2024-01-01', hour: 10, completed_count: 5 },
                    { date: '2024-01-01', hour: 11, completed_count: 3 },
                    { date: '2024-01-01', hour: 14, completed_count: 4 },
                    { date: '2024-01-01', hour: 15, completed_count: 6 },
                    { date: '2024-01-02', hour: 9, completed_count: 1 },
                    { date: '2024-01-02', hour: 10, completed_count: 7 },
                    { date: '2024-01-02', hour: 11, completed_count: 4 },
                    { date: '2024-01-02', hour: 14, completed_count: 3 },
                    { date: '2024-01-02', hour: 16, completed_count: 5 },
                    { date: '2024-01-03', hour: 8, completed_count: 2 },
                    { date: '2024-01-03', hour: 10, completed_count: 6 },
                    { date: '2024-01-03', hour: 15, completed_count: 8 },
                  ],
                  date_range: {
                    start_date: '2024-01-01',
                    end_date: '2024-01-03'
                  },
                  max_count: 8
                }}
              />
            </DemoCard>

            {/* Dashboard Widgets */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <DemoCard title="Peak Performance" description="Best performing time slots">
                <PeakPerformanceHours
                  data={[
                    { timeRange: "9:00 AM - 11:00 AM", percentage: 92, color: designTokens.colors.feedback.success },
                    { timeRange: "2:00 PM - 4:00 PM", percentage: 78, color: designTokens.colors.primary[600] },
                    { timeRange: "7:00 PM - 9:00 PM", percentage: 65, color: designTokens.colors.feedback.warning }
                  ]}
                />
              </DemoCard>

              <DemoCard title="Focus Time Analysis" description="Deep work vs meetings breakdown">
                <FocusTimeAnalysis
                  averageFocus="5.2h"
                  subtitle="Average Daily Focus"
                  activities={[
                    { name: "Deep Work", value: 3.5, color: designTokens.colors.secondary[600] },
                    { name: "Meetings", value: 1.7, color: designTokens.colors.primary[600] }
                  ]}
                />
              </DemoCard>

              <DemoCard title="Weekly Goals" description="Progress towards weekly targets">
                <WeeklyGoalsProgress
                  goals={[
                    { name: "Complete 40 tasks", current: 35, target: 40, color: designTokens.colors.feedback.success },
                    { name: "Attend 6 meetings", current: 4, target: 6, color: designTokens.colors.primary[600] },
                    { name: "Create 15 notes", current: 18, target: 15, color: designTokens.colors.secondary[600] }
                  ]}
                />
              </DemoCard>
            </div>

            {/* Activity and Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DemoCard title="Recent Activity" description="Latest user actions and updates">
                <RecentActivity
                  activities={[
                    { 
                      text: "Completed: Design system update", 
                      time: "2 hours ago", 
                      icon: "fa-solid fa-check-circle",
                      iconColor: designTokens.colors.feedback.success
                    },
                    { 
                      text: "New meeting scheduled", 
                      time: "4 hours ago", 
                      icon: "fa-solid fa-calendar",
                      iconColor: designTokens.colors.primary[600]
                    },
                    { 
                      text: "Added note: Project requirements", 
                      time: "6 hours ago", 
                      icon: "fa-solid fa-sticky-note",
                      iconColor: designTokens.colors.secondary[600]
                    }
                  ]}
                />
              </DemoCard>

              <DemoCard title="AI Insights" description="Intelligent productivity recommendations">
                <AIInsights
                  insights={[
                    {
                      text: "Your productivity peaked on Tuesday this week with 92% task completion.",
                      emoji: "📈"
                    },
                    {
                      text: "Consider scheduling fewer meetings on Fridays to boost focus time by 25%.",
                      emoji: "🎯"
                    },
                    {
                      text: "You've created 60% more notes this month compared to last month.",
                      emoji: "📝"
                    }
                  ]}
                />
              </DemoCard>
            </div>

            {/* Layout Examples */}
            <DemoCard title="Dashboard Layouts" description="Grid layouts for organizing dashboard content">
              <div className="space-y-6">
                <div>
                  <Small color="secondary" className="uppercase tracking-wide mb-3 block">Stats Grid (4 columns)</Small>
                  <StatsGrid cols={4}>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center">
                      <Small>Stat 1</Small>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg text-center">
                      <Small>Stat 2</Small>
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg text-center">
                      <Small>Stat 3</Small>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-center">
                      <Small>Stat 4</Small>
                    </div>
                  </StatsGrid>
                </div>

                <div>
                  <Small color="secondary" className="uppercase tracking-wide mb-3 block">Charts Grid (2 columns)</Small>
                  <ChartsGrid cols={2}>
                    <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg text-center border">
                      <Small>Chart Area 1</Small>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg text-center border">
                      <Small>Chart Area 2</Small>
                    </div>
                  </ChartsGrid>
                </div>
              </div>
            </DemoCard>
          </div>
        </Section>

        {/* Navigation Section */}
        <Section title="Navigation" id="navigation">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DemoCard title="Desktop Sidebar" description="Full sidebar navigation for desktop and tablet">
              <div className="w-64 h-96 border rounded-lg overflow-hidden">
                <Sidebar
                  activeItem="tasks"
                  user={{
                    name: "John Smith",
                    email: "john@example.com"
                  }}
                  className="h-full"
                />
              </div>
            </DemoCard>

            <DemoCard title="Collapsed Sidebar" description="Collapsed sidebar for space-saving layout">
              <div className="w-16 h-96 border rounded-lg overflow-hidden">
                <Sidebar
                  collapsed={true}
                  activeItem="tasks"
                  user={{
                    name: "John Smith",
                    email: "john@example.com"
                  }}
                  className="h-full"
                />
              </div>
            </DemoCard>

            <DemoCard title="Mobile Header" description="Mobile navigation header with menu toggle">
              <div className="space-y-4">
                <div className="border rounded-lg overflow-hidden">
                  <MobileHeader
                    title="Dashboard"
                    user={{
                      name: "John Smith",
                      email: "john@example.com"
                    }}
                    onMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
                  />
                </div>
                
                <Small color="secondary">
                  The mobile header automatically shows on small screens and provides a hamburger menu button.
                </Small>
              </div>
            </DemoCard>

            <DemoCard title="Responsive Features" description="Built-in responsive behavior">
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start space-x-3">
                      <i className="fa-solid fa-mobile text-primary-600 mt-1" />
                      <div>
                        <div className="font-medium">Mobile (&lt; 768px)</div>
                        <div className="text-gray-600">Hidden sidebar with mobile header and overlay menu</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <i className="fa-solid fa-tablet text-primary-600 mt-1" />
                      <div>
                        <div className="font-medium">Tablet (&gt; 768px)</div>
                        <div className="text-gray-600">Full or collapsed sidebar depending on preference</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <i className="fa-solid fa-desktop text-primary-600 mt-1" />
                      <div>
                        <div className="font-medium">Desktop</div>
                        <div className="text-gray-600">Full sidebar with all features visible</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-green-50 rounded border border-green-200">
                  <Small className="text-green-800 font-medium">✅ Responsive Design:</Small>
                  <Small color="secondary" className="block mt-1">
                    The navigation automatically adapts to different screen sizes using CSS breakpoints.
                  </Small>
                </div>
              </div>
            </DemoCard>
          </div>
        </Section>

        {/* Dashboard Section */}
        <Section title="Dashboard Components" id="dashboard">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DemoCard title="Category Overview" description="Category cards showing item counts and associated numbers">
              <div className="space-y-3">
                <CategoryCard
                  name="Work"
                  itemCount={12}
                  associatedNumber={8}
                  type="work"
                />
                
                <CategoryCard
                  name="Ideas"
                  itemCount={5}
                  associatedNumber={2}
                  type="ideas"
                />
                
                <CategoryCard
                  name="Personal"
                  itemCount={8}
                  associatedNumber={3}
                  type="personal"
                />
                
                <CategoryCard
                  name="Marketing"
                  itemCount={4}
                  associatedNumber={1}
                  type="marketing"
                />
                
                <CategoryCard
                  name="Add Category"
                  itemCount={0}
                  showAdd={true}
                  type="development"
                />
              </div>
            </DemoCard>

            <DemoCard title="Statistics Overview" description="Progress stats and productivity metrics">
              <StatsCard title="Overview">
                <ProgressStats
                  completed={24}
                  inProgress={18}
                  completedChange="12%"
                  inProgressChange="5%"
                />
                
                <ProductivityScore score={78} />
                
                <UpcomingDeadlines
                  deadlines={[
                    { title: "Marketing Plan", dueDate: "Tomorrow", priority: "high" },
                    { title: "Client Presentation", dueDate: "In 3 days", priority: "medium" }
                  ]}
                />
              </StatsCard>
            </DemoCard>
          </div>
        </Section>

        {/* Alerts Section */}
        <Section title="Alerts" id="alerts">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DemoCard title="Alert Types" description="Different alert variants for various messages">
              <div className="space-y-4">
                <SuccessAlert title="Success!">
                  Your task has been completed successfully.
                </SuccessAlert>
                
                <InfoAlert title="Information">
                  New AI suggestions are available for your tasks.
                </InfoAlert>
                
                <WarningAlert title="Warning">
                  This action cannot be undone. Please proceed with caution.
                </WarningAlert>
                
                <ErrorAlert title="Error">
                  Something went wrong. Please try again or contact support.
                </ErrorAlert>
              </div>
            </DemoCard>

            <DemoCard title="Alert Features" description="Dismissible alerts and custom styling">
              <div className="space-y-4">
                {showAlert && (
                  <Alert
                    variant="info"
                    title="Dismissible Alert"
                    dismissible
                    onDismiss={() => setShowAlert(false)}
                  >
                    Click the × button to dismiss this alert.
                  </Alert>
                )}
                
                <Alert variant="success" title="Without Title">
                  This alert doesn't have a title, just the message content.
                </Alert>
                
                {!showAlert && (
                  <Button 
                    size="sm" 
                    variant={darkMode ? "accent-outline" : "primary-outline"}
                    onClick={() => setShowAlert(true)}
                  >
                    Restore Dismissed Alert
                  </Button>
                )}
              </div>
            </DemoCard>
          </div>
        </Section>

        {/* Spacing Section */}
        <Section title="Spacing" id="spacing">
          <DemoCard title="Spacing Scale" description="Consistent spacing values throughout the system">
            <div className="space-y-4">
              {Object.entries(designTokens.spacing).map(([key, value]) => (
                <div key={key} className="flex items-center space-x-4">
                  <div className="w-16 text-right">
                    <Caption className="font-mono">{key}</Caption>
                  </div>
                  <div 
                    className="bg-primary-200 rounded"
                    style={{ 
                      width: value,
                      height: '1rem',
                      minWidth: '4px'
                    }}
                  />
                  <Caption color="secondary" className="font-mono">{value}</Caption>
                </div>
              ))}
            </div>
          </DemoCard>
        </Section>

        {/* Footer */}
        <footer className="text-center py-12 border-t border-gray-200 dark:border-dark-300 mt-16">
          <Logo className="justify-center mb-4" />
          <Body color="secondary">
            OrganiseWise Design System v2.0 - {darkMode ? 'Dark Mode' : 'Light Mode'}
          </Body>
          <Caption color="tertiary" className="mt-2">
            Built with React, Tailwind CSS, class-variance-authority, and ❤️
          </Caption>
        </footer>
      </div>
    </div>
  );
};

export default DesignSystemShowcase; 